import React, { useState } from "react";
import { PropTypes } from "prop-types";
import { MrbRouterLink } from "mrb/core/infrastructure/router";
import { MrbSelect } from "mrb/components/select";
import pulseDevice from "themes/assets/images/device-pulse.png";
import boltDevice from "themes/assets/images/device-bolt.png";
import appStoreBadge from "themes/assets/images/app-store-badge.png";
import googlePlayBadge from "themes/assets/images/google-play-badge.png";

export function AppBadges({ t }) {
    return (
        <div className="c-device-onboarding__badges">
            <a
                href="https://apps.apple.com/us/app/naki-go/id1612244728"
                target="_blank"
                rel="noopener noreferrer"
            >
                <img src={appStoreBadge} alt={t("APPLICATION.PAYMENT.PAYMENT_SUCCESS.APP.APP_STORE")} />
            </a>
            <a
                href="https://play.google.com/store/apps/details?id=com.nakiradio.mobile&hl=en"
                target="_blank"
                rel="noopener noreferrer"
            >
                <img src={googlePlayBadge} alt={t("APPLICATION.PAYMENT.PAYMENT_SUCCESS.APP.PLAY_STORE")} />
            </a>
            {/* TODO: enable once the Windows app ships (needs download URL)
            <a className="c-btn c-btn--ghost c-btn--sml" href="TODO_WINDOWS_URL" target="_blank" rel="noopener noreferrer">
                {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.APP.WINDOWS")}
            </a>
            */}
        </div>
    );
}

AppBadges.propTypes = {
    t: PropTypes.func.isRequired,
};

export function ContentModeControl({ modeSelectStore, onInfoClick, t, portalMenu = true }) {
    return (
        <React.Fragment>
            <div className="c-device-onboarding__label-row">
                <p className="c-device-onboarding__label">
                    {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.CONTENT_MODE.TITLE")}
                </p>
                <button type="button" className="c-device-onboarding__info-btn" onClick={onInfoClick}>
                    {t("APPLICATION.USER_SETTINGS.EDIT.FIELDS.CERTIFIED_MODE.INFO_BUTTON")}
                </button>
            </div>
            <div className="c-device-onboarding__mode">
                <MrbSelect
                    classNameExtend="c-dropdown--dark c-dropdown--sml-custom"
                    t={t}
                    store={modeSelectStore}
                    menuPortalTarget={portalMenu ? document.body : undefined}
                />
            </div>
        </React.Fragment>
    );
}

ContentModeControl.propTypes = {
    modeSelectStore: PropTypes.object.isRequired,
    onInfoClick: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    portalMenu: PropTypes.bool,
};

/**
 * Interactive "set up your device" flow shared by the Devices empty state
 * and the post-payment success modal: intro -> choose device -> Bolt steps.
 */
export function DeviceSetupFlow({ t }) {
    const [step, setStep] = useState("intro");

    if (step === "choose") {
        return (
            <div className="c-device-onboarding__tile c-device-onboarding__tile--hero">
                <button type="button" className="c-device-onboarding__back" onClick={() => setStep("intro")}>
                    {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.CHOOSE.BACK")}
                </button>
                <h4 className="c-device-onboarding__hero-title">
                    {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.CHOOSE.QUESTION")}
                </h4>
                <div className="c-device-onboarding__choices">
                    <div className="c-device-onboarding__choice">
                        <img className="c-device-onboarding__choice-thumb" src={pulseDevice} alt="" />
                        <div className="c-device-onboarding__choice-body">
                            <p className="c-device-onboarding__choice-title">
                                {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.CHOOSE.PLAYER_TITLE")}
                            </p>
                            <p className="c-device-onboarding__choice-desc">
                                {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.CHOOSE.PLAYER_DESC")}
                            </p>
                        </div>
                        <MrbRouterLink
                            className="c-btn c-btn--primary c-btn--med"
                            routeName="master.application.device.create"
                        >
                            {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.CHOOSE.PLAYER_ACTION")}
                        </MrbRouterLink>
                    </div>

                    <div className="c-device-onboarding__choice">
                        <img className="c-device-onboarding__choice-thumb" src={boltDevice} alt="" />
                        <div className="c-device-onboarding__choice-body">
                            <p className="c-device-onboarding__choice-title">
                                {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.CHOOSE.BOLT_TITLE")}
                            </p>
                            <p className="c-device-onboarding__choice-desc">
                                {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.CHOOSE.BOLT_DESC")}
                            </p>
                        </div>
                        <button
                            type="button"
                            className="c-btn c-btn--ghost c-btn--med"
                            onClick={() => setStep("bolt")}
                        >
                            {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.CHOOSE.BOLT_ACTION")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (step === "bolt") {
        return (
            <div className="c-device-onboarding__tile c-device-onboarding__tile--hero">
                <button type="button" className="c-device-onboarding__back" onClick={() => setStep("choose")}>
                    {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.BOLT.BACK")}
                </button>
                <h4 className="c-device-onboarding__hero-title">
                    {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.BOLT.TITLE")}
                </h4>
                <p className="c-device-onboarding__hero-desc">
                    {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.BOLT.DESCRIPTION")}
                </p>
                <ol className="c-device-onboarding__steps-list">
                    <li>{t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.BOLT.STEP_1")}</li>
                    <li>{t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.BOLT.STEP_2")}</li>
                    <li>{t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.BOLT.STEP_3")}</li>
                    <li>{t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.BOLT.STEP_4")}</li>
                </ol>
            </div>
        );
    }

    return (
        <div className="c-device-onboarding__tile c-device-onboarding__tile--hero">
            <div className="c-device-onboarding__stage">
                <div className="c-device-onboarding__devices-duo">
                    <img src={pulseDevice} alt="Naki Pulse" />
                    <img className="c-device-onboarding__device--flip" src={boltDevice} alt="Naki Bolt" />
                </div>
            </div>
            <h4 className="c-device-onboarding__hero-title">
                {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.INTRO.QUESTION")}
            </h4>
            <p className="c-device-onboarding__hero-desc">
                {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.INTRO.DESCRIPTION")}
            </p>
            <div className="c-device-onboarding__actions">
                <button type="button" className="c-btn c-btn--primary c-btn--med" onClick={() => setStep("choose")}>
                    {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.INTRO.CONNECT")}
                </button>
                <a
                    className="c-btn c-btn--ghost c-btn--med"
                    href="https://naki.audio/naki-devices/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t("APPLICATION.DEVICE_SETTINGS.ONBOARDING.INTRO.SHOP")}
                </a>
            </div>
        </div>
    );
}

DeviceSetupFlow.propTypes = {
    t: PropTypes.func.isRequired,
};
